import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loading from 'Components/Atoms/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import { CATEGORY_COLORS, DRAWER_STACK } from 'Utils/constants';
import useDrawerStack from 'Hooks/useDrawerStack';
import { createCategory } from 'Services/categories';
import useTaxonomies from 'Hooks/useTaxonomies';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import Search from 'Components/Atoms/Search/Search';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import { useEffect, useState, Fragment } from 'react';
import { cn } from 'Utils/cn';
import useCategories from 'Hooks/useCategories';
import { hasMatch } from 'Utils/strings';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import Button from 'Components/Atoms/Button/Button';
import DrawerStackFooter from 'Components/Organisms/DrawerStackProvider/DrawerStackFooter';

function filterByText(category, text) {
    const hasText = hasMatch(category.name, text);
    const hasSubcategoryText = category.subcategories.some((subcategory) => {
        return hasMatch(subcategory.name, text);
    });

    return hasText || hasSubcategoryText;
}

export default function TagsSelectorDrawer(props) {
    const { selectedTagsIds, onSubmit, onCancel, onError } = props;

    const { t } = useTranslation('categories');
    const queryClient = useQueryClient();

    const { open } = useDrawerStack();
    const { taxonomies, isLoading: isTaxonomiesLoading } = useTaxonomies();
    const { categories, isLoading: isCategoriesLoading } = useCategories();

    const [selectedIds, setSelectedIds] = useState(() => selectedTagsIds ?? []);
    const [processing, setProcessing] = useState(false);
    const [currentTaxonomy, setCurrentTaxonomy] = useState(taxonomies[0]?.id ?? '');
    const [filterText, setFilterText] = useState('');

    const parentCategories = categories.filter((category) => !category.parent_tag_id);
    const formattedCategories = parentCategories.map((category) => ({
        ...category,
        subcategories: categories.filter((subcategory) => subcategory.parent_tag_id === category.id),
    }));

    const categoriesFiltered = formattedCategories
        .filter((category) => category.taxonomy_id === currentTaxonomy)
        .filter((category) => filterByText(category, filterText))
        .map((category) => ({
            ...category,
            subcategories: category.subcategories.filter((subcategory) => hasMatch(subcategory.name, filterText)),
        }));

    const onChangeHandle = (id, isAdd) => {
        if (isAdd) {
            setSelectedIds((ids) => [...ids, id]);
        } else {
            const subcategoriesIds = formattedCategories
                .find((category) => category.id === id)
                .subcategories.map((subcategory) => subcategory.id);
            setSelectedIds((ids) => ids.filter((tagId) => tagId !== id && !subcategoriesIds.includes(tagId)));
        }
    };

    const onChangeSubcategoryHandle = ([parentId, id], isAdd) => {
        if (isAdd) {
            setSelectedIds((ids) => {
                const idsToAdd = ids.some((tagId) => tagId === parentId) ? [id] : [parentId, id];
                return [...ids, ...idsToAdd];
            });
        } else {
            setSelectedIds((ids) => ids.filter((tagId) => tagId !== id));
        }
    };

    useEffect(() => {
        if (!isTaxonomiesLoading) {
            setCurrentTaxonomy(taxonomies[0]?.id ?? '');
        }
    }, [isTaxonomiesLoading]);

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR, {
            title: t('create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                setSelectedIds((ids) => [...ids, tag.id]);
                setCurrentTaxonomy(tag.taxonomy_id);
            },
        });
    };

    const handleAddSubTag = (tag) => {
        open(DRAWER_STACK.SUBTAG_EDITOR, {
            title: t('create-subTag'),
            onSubmit: async (data) => {
                const subtag = await createCategory({
                    icon: tag.icon,
                    color: tag.color,
                    parent_tag_id: tag.id,
                    taxonomy_id: tag.taxonomy_id,
                    ...data,
                });
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                onChangeSubcategoryHandle([tag.id, subtag.id], true);
                setCurrentTaxonomy(subtag.taxonomy_id);
            },
            subTag: {},
        });
    };

    const handleAccept = async () => {
        setProcessing(true);
        try {
            await onSubmit(selectedIds);
        } catch (error) {
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    const handleChangeTaxonomy = (id) => {
        setFilterText('');
        setCurrentTaxonomy(id);
    };

    const isLoading = isCategoriesLoading || isTaxonomiesLoading;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-6">
                <Loading />
            </div>
        );
    }

    return (
        <div>
            {taxonomies.length > 1 && (
                <Tabs value={currentTaxonomy} onChange={handleChangeTaxonomy}>
                    {taxonomies.map((taxonomy) => (
                        <Tab key={taxonomy.id} value={taxonomy.id}>
                            {taxonomy.name}
                        </Tab>
                    ))}
                </Tabs>
            )}

            <div className="py-3 flex items-center gap-2">
                <Search
                    placeholder={t('search-placeholder')}
                    className="grow"
                    value={filterText}
                    onChange={setFilterText}
                    data-autofocus="true"
                />

                <IconButton tooltip={t('add-category')} icon={<OutlineAdd />} onClick={handleAddTag} />
            </div>

            <div>
                {categoriesFiltered.map((category) => (
                    <Fragment key={category.id}>
                        <div className="group flex items-center py-1.5">
                            <label className="flex grow gap-4 items-center">
                                <Checkbox
                                    checked={selectedIds.includes(category.id)}
                                    label={category.name}
                                    onChange={(e) => onChangeHandle(category.id, e.target.checked)}
                                />
                                <div
                                    className="rounded-lg aspect-square grid place-content-center text-xl w-[42px]"
                                    style={{ backgroundColor: category.bgColor }}
                                >
                                    {category.icon}
                                </div>
                                <div className="font-bold" style={{ color: category.color }}>
                                    {category.name}
                                </div>
                            </label>

                            <Tooltip label={t('add-subcategory')}>
                                <button
                                    style={{ backgroundColor: category.bgColor }}
                                    className={cn(
                                        'inline-flex size-6 items-center justify-center rounded-full',
                                        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                                        'md:opacity-0 group-hover:opacity-100 transition-opacity',
                                    )}
                                    onClick={() => handleAddSubTag(category)}
                                >
                                    <OutlineAdd size={18} />
                                </button>
                            </Tooltip>
                        </div>
                        {category.subcategories.length > 0 && (
                            <div className="flex flex-wrap gap-x-1.5 gap-y-1 mb-5 pl-10">
                                {category.subcategories.map((subcategory) => (
                                    <div key={subcategory.id}>
                                        <button
                                            style={{ '--color': subcategory.color }}
                                            type="button"
                                            onClick={() =>
                                                onChangeSubcategoryHandle(
                                                    [category.id, subcategory.id],
                                                    !selectedIds.includes(subcategory.id),
                                                )
                                            }
                                            className={cn(
                                                'inline-flex text-xs px-3 py-1 rounded-full text-[var(--color)] inset-ring-1 inset-ring-current/50',
                                                selectedIds.includes(subcategory.id) &&
                                                    'bg-[var(--color)] text-white inset-ring-0',
                                            )}
                                        >
                                            {subcategory.name}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Fragment>
                ))}
            </div>

            <DrawerStackFooter>
                <Button variant="primary" isBusy={processing} type="button" onClick={handleAccept} className="grow">
                    {t('accept')}
                </Button>
                <Button onClick={onCancel} className="grow">
                    {t('cancel')}
                </Button>
            </DrawerStackFooter>
        </div>
    );
}

TagsSelectorDrawer.propTypes = {
    selectedTagsIds: PropTypes.arrayOf(PropTypes.string),
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    onError: PropTypes.func,
    title: PropTypes.string,
};
